import type {
  InvivoLegacyNodeMaterialPlan,
  InvivoMaterialPlan,
  InvivoSceneDefinition,
  InvivoSceneMaterialDefinition,
  InvivoSceneTexturePlan,
} from "../../types/invivo_scene";
import { buildLegacyStylePreset, builtinLegacyMaterialDefinition } from "../../legacy_compat/material_catalog";
import { supportedLegacyNodeMaterialInputs } from "../../legacy_compat/node_material_values";

interface MaterialTextureOptions {
  filename: string;
  invert_y?: boolean;
  u_scale?: number;
  v_scale?: number;
}

function resolveAssetUrl({
  assetBaseUrl,
  textureBaseUrl,
  sharedAssetBaseUrl,
  availableTextureFiles,
  filename,
}: {
  assetBaseUrl: string;
  textureBaseUrl?: string;
  sharedAssetBaseUrl?: string;
  availableTextureFiles: Set<string>;
  filename: string | undefined;
}): string | undefined {
  if (!filename) {
    return undefined;
  }

  if (filename.startsWith("shared/")) {
    return new URL(filename.slice("shared/".length), sharedAssetBaseUrl || new URL("../../shared/", assetBaseUrl)).toString();
  }

  if (!availableTextureFiles.has(filename)) {
    return undefined;
  }

  return new URL(filename, textureBaseUrl || assetBaseUrl).toString();
}

function textureOptions(value: unknown): MaterialTextureOptions | undefined {
  if (typeof value === "string") {
    return {
      filename: value,
    };
  }

  if (!value || typeof value !== "object") {
    return undefined;
  }

  return typeof (value as { filename?: unknown }).filename === "string"
    ? (value as MaterialTextureOptions)
    : undefined;
}

function textureFilename(value: unknown): string | undefined {
  return textureOptions(value)?.filename;
}

function buildTexturePlan({
  assetBaseUrl,
  textureBaseUrl,
  sharedAssetBaseUrl,
  value,
}: {
  assetBaseUrl: string;
  textureBaseUrl?: string;
  sharedAssetBaseUrl?: string;
  value: unknown;
}): InvivoSceneTexturePlan | undefined {
  const options = textureOptions(value);

  if (!options) {
    return undefined;
  }

  const url = options.filename.startsWith("shared/")
    ? new URL(
        options.filename.slice("shared/".length),
        sharedAssetBaseUrl || new URL("../../shared/", assetBaseUrl)
      ).toString()
    : new URL(options.filename, textureBaseUrl || assetBaseUrl).toString();

  return {
    url,
    invertY: options.invert_y ?? false,
    uScale: options.u_scale ?? 1,
    vScale: options.v_scale ?? 1,
  };
}

function resolveMaterialDefinition(
  definition: InvivoSceneDefinition,
  materialName: string
): InvivoSceneMaterialDefinition {
  return {
    ...(builtinLegacyMaterialDefinition(materialName) || {}),
    ...(definition.materials?.[materialName] || {}),
  };
}

export function resolveInvivoMaterialPlan({
  definition,
  assetBaseUrl,
  textureBaseUrl,
  sharedAssetBaseUrl,
  availableTextureFiles,
  materialName,
}: {
  definition: InvivoSceneDefinition;
  assetBaseUrl: string;
  textureBaseUrl?: string;
  sharedAssetBaseUrl?: string;
  availableTextureFiles?: string[];
  materialName: string;
}): InvivoMaterialPlan {
  const resolvedTextureBaseUrl = textureBaseUrl || assetBaseUrl;
  const stagedTextureFiles = new Set(availableTextureFiles || []);
  const materialDefinition = resolveMaterialDefinition(definition, materialName);
  const stylePreset = buildLegacyStylePreset(materialName);
  const program = typeof materialDefinition.program === "string" ? materialDefinition.program : "PBRMetallicRoughnessMaterial";
  const backFaceCulling =
    typeof materialDefinition.backfaces === "boolean"
      ? !materialDefinition.backfaces
      : stylePreset.backFaceCulling ?? true;

  const transparencyMode =
    typeof materialDefinition.opacity === "number" && materialDefinition.opacity < 1 ? "alpha_blend" : "opaque";

  if (program === "sheet_material.json") {
    const nodeMaterialInputs = supportedLegacyNodeMaterialInputs(materialDefinition);

    return {
      kind: "sheet",
      name: materialName,
      alpha: typeof materialDefinition.opacity === "number" ? materialDefinition.opacity : stylePreset.alpha ?? 0.9,
      backFaceCulling,
      transparencyMode,
      nodeMaterialInputs,
      baseColor: typeof nodeMaterialInputs.baseColor === "string" ? nodeMaterialInputs.baseColor : undefined,
      previewTexture: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.preview }),
      albedoRoughnessTexture: buildTexturePlan({
        assetBaseUrl,
        textureBaseUrl: resolvedTextureBaseUrl,
        sharedAssetBaseUrl,
        value: materialDefinition.AlbedoRoughness,
      }),
      xycaTexture: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.XYCA }),
      dotsTexture: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.Dots }),
      crumplesTexture: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.Crumples }),
      creasesTexture: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.Creases }),
      fibersTexture: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.Fibers }),
      previewTextureUrl: resolveAssetUrl({
        assetBaseUrl,
        textureBaseUrl: resolvedTextureBaseUrl,
        sharedAssetBaseUrl,
        availableTextureFiles: stagedTextureFiles,
        filename: textureFilename(materialDefinition.preview),
      }),
      albedoRoughnessTextureUrl: resolveAssetUrl({
        assetBaseUrl,
        textureBaseUrl: resolvedTextureBaseUrl,
        sharedAssetBaseUrl,
        availableTextureFiles: stagedTextureFiles,
        filename: textureFilename(materialDefinition.AlbedoRoughness),
      }),
      xycaTextureUrl: resolveAssetUrl({
        assetBaseUrl,
        textureBaseUrl: resolvedTextureBaseUrl,
        sharedAssetBaseUrl,
        availableTextureFiles: stagedTextureFiles,
        filename: textureFilename(materialDefinition.XYCA),
      }),
      dotsTextureUrl: resolveAssetUrl({
        assetBaseUrl,
        textureBaseUrl: resolvedTextureBaseUrl,
        sharedAssetBaseUrl,
        availableTextureFiles: stagedTextureFiles,
        filename: textureFilename(materialDefinition.Dots),
      }),
      crumplesTextureUrl: resolveAssetUrl({
        assetBaseUrl,
        textureBaseUrl: resolvedTextureBaseUrl,
        sharedAssetBaseUrl,
        availableTextureFiles: stagedTextureFiles,
        filename: textureFilename(materialDefinition.Crumples),
      }),
      creasesTextureUrl: resolveAssetUrl({
        assetBaseUrl,
        textureBaseUrl: resolvedTextureBaseUrl,
        sharedAssetBaseUrl,
        availableTextureFiles: stagedTextureFiles,
        filename: textureFilename(materialDefinition.Creases),
      }),
      fibersTextureUrl: resolveAssetUrl({
        assetBaseUrl,
        textureBaseUrl: resolvedTextureBaseUrl,
        sharedAssetBaseUrl,
        availableTextureFiles: stagedTextureFiles,
        filename: textureFilename(materialDefinition.Fibers),
      }),
      tilingDots: typeof nodeMaterialInputs.tilingDots === "object" && "x" in nodeMaterialInputs.tilingDots ? nodeMaterialInputs.tilingDots : undefined,
      tilingCreases: typeof nodeMaterialInputs.tilingCreases === "object" && "x" in nodeMaterialInputs.tilingCreases ? nodeMaterialInputs.tilingCreases : undefined,
      tilingCrumples: typeof nodeMaterialInputs.tilingCrumples === "object" && "x" in nodeMaterialInputs.tilingCrumples ? nodeMaterialInputs.tilingCrumples : undefined,
      tilingMultiplier: typeof nodeMaterialInputs.tilingMultiplier === "number" ? nodeMaterialInputs.tilingMultiplier : undefined,
    };
  }

  if (program !== "PBRMetallicRoughnessMaterial") {
    const nodeMaterialInputs = supportedLegacyNodeMaterialInputs(materialDefinition);
    const textureBindings: InvivoLegacyNodeMaterialPlan["textureBindings"] = {
      XYRA: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.XYRA }),
      Mask: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.Mask }),
      Albedo: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.Albedo || materialDefinition.preview }),
      AO: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.AO }),
      XYRS: buildTexturePlan({ assetBaseUrl, textureBaseUrl: resolvedTextureBaseUrl, sharedAssetBaseUrl, value: materialDefinition.XYRS }),
    };

    return {
      kind: "legacy_node",
      name: materialName,
      program,
      alpha: typeof materialDefinition.opacity === "number" ? materialDefinition.opacity : stylePreset.alpha ?? 1,
      backFaceCulling,
      transparencyMode,
      nodeMaterialInputs,
      textureBindings,
    };
  }

  const baseTextureUrl = resolveAssetUrl({
    assetBaseUrl,
    textureBaseUrl: resolvedTextureBaseUrl,
    sharedAssetBaseUrl,
    availableTextureFiles: stagedTextureFiles,
    filename: textureFilename(materialDefinition.preview) || `${materialName}_Albedo.png`,
  });

  return {
    kind: "standard_pbr",
    name: materialName,
    alpha: typeof materialDefinition.opacity === "number" ? materialDefinition.opacity : 1,
    metallic: typeof materialDefinition.metallic === "number" ? materialDefinition.metallic : stylePreset.metallic,
    roughness: typeof materialDefinition.roughness === "number" ? materialDefinition.roughness : stylePreset.roughness,
    backFaceCulling,
    baseColor: typeof materialDefinition.baseColor === "string" ? materialDefinition.baseColor : baseTextureUrl ? "#ffffff" : stylePreset.baseColor,
    baseTextureUrl,
    normalTextureUrl: resolveAssetUrl({
      assetBaseUrl,
      textureBaseUrl: resolvedTextureBaseUrl,
      sharedAssetBaseUrl,
      availableTextureFiles: stagedTextureFiles,
      filename: `${materialName}_NNN.png`,
    }),
    metallicRoughnessTextureUrl: resolveAssetUrl({
      assetBaseUrl,
      textureBaseUrl: resolvedTextureBaseUrl,
      sharedAssetBaseUrl,
      availableTextureFiles: stagedTextureFiles,
      filename: `${materialName}_ARM.png`,
    }),
    occlusionTextureUrl: resolveAssetUrl({
      assetBaseUrl,
      textureBaseUrl: resolvedTextureBaseUrl,
      sharedAssetBaseUrl,
      availableTextureFiles: stagedTextureFiles,
      filename: `${materialName}_ARM.png`,
    }),
    roughnessTextureChannel: "green",
    metallicTextureChannel: "blue",
    useAmbientInGrayScale: true,
    useAlphaFromAlbedoTexture: false,
    transparencyMode,
  };
}
